import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Edit2, Printer } from "lucide-react";
import { ClipboardList } from "lucide-react";
import Loader from "../components/Loader";
import { useOutsideClick } from "../utils/useOutsideClick";
import { fetchSettings } from "../apis/settings";
import {
  fetchInspectionItems,
  createInspectionReport,
  updateInspectionReport,
} from "../apis/inspectionCard";
import { displayToast } from "../utils/Toast";
import { usePermissions } from "../hooks/usePermissions";
import { useSalesFormStore } from "../services/useSalesFormStore";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import "../styles/components/InspectionCardPopup.css";
import "../styles/components/TransactionTable.css";

const InspectionCardPopup = ({
  selectedFieldName = "",
  jobCardNo = "",
  inspectionHeadData,
  onClose,
  jobCardCreated = false,
  setInspectionPopupState,
  job_card_id,
  addMode = false,
  transactionCode = "",
}) => {
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const rights = usePermissions(transactionCode);
  const formData = useSalesFormStore((state) => state.formData);
  const [rows, setRows] = useState([
    {
      parameter: "",
      parameter_value: null,
      tolerence: null,
      value_1: null,
      value_2: null,
      value_3: null,
      value_4: null,
      value_5: null,
      status: "",
    },
  ]);
  const [parametersOptions, setParametersOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState({ row: null, field: null });
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(180);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [headData, setHeadData] = useState({
    product_id: inspectionHeadData?.product_id,
    product_name: inspectionHeadData?.product_name,
    product_desc: inspectionHeadData?.product_desc,
    tran_no: jobCardNo ? String(jobCardNo) : "",
    quantity: inspectionHeadData?.total_quantity,
    tran_code: "JC",
    tran_id: inspectionHeadData?.tran_id,
  });
  const [localToast, setLocalToast] = useState({ message: "", type: "" });
  const showLocalToast = (message, type = "error") => {
    setLocalToast({ message, type });
    setTimeout(() => setLocalToast({ message: "", type: "" }), 2500);
  };

  useEffect(() => {
    setIsEditMode(!addMode);
  }, [addMode]);

  useEffect(() => {
    if (jobCardCreated && job_card_id) {
      (async () => {
        setIsLoading(true);
        try {
          const response = await fetchInspectionItems({
            id: job_card_id,
            tranCode: "JC",
            selectedInspection: selectedFieldName,
          });

          if (response && response.head_data) {
            setHeadData(response.head_data);
            setRows(
              Array.isArray(response.item_data)
                ? response.item_data.map((item) => ({
                    parameter: item.parameter || "",
                    parameter_value: item.parameter_value ?? null,
                    tolerence: item.tolerence ?? null,
                    value_1: item.value_1 ?? null,
                    value_2: item.value_2 ?? null,
                    value_3: item.value_3 ?? null,
                    value_4: item.value_4 ?? null,
                    value_5: item.value_5 ?? null,
                    status: item.status || "",
                  }))
                : []
            );
          }
        } catch (err) {
          displayToast("Failed to fetch inspection report", "error");
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [jobCardCreated, job_card_id]);

  useEffect(() => {
    if (openDropdown.row === null) return;
    function handleClick(e) {
      const triggerKey = `${openDropdown.field}-${openDropdown.row}`;
      const triggerEl = dropdownRefs.current[triggerKey];
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target) &&
        (!triggerEl || !triggerEl.contains(e.target))
      ) {
        setOpenDropdown({ row: null, field: null });
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openDropdown]);

  useEffect(() => {
    const getMaxWidth = (options) => {
      if (!options || options.length === 0) return 180;
      const temp = document.createElement("span");
      temp.style.visibility = "hidden";
      temp.style.position = "absolute";
      temp.style.fontSize = "16px";
      temp.style.fontFamily = "inherit";
      document.body.appendChild(temp);
      let max = 180;
      options.forEach((opt) => {
        temp.textContent = opt.name;
        max = Math.max(max, temp.offsetWidth + 48);
      });
      document.body.removeChild(temp);
      return max;
    };
    const opsW = getMaxWidth(parametersOptions);
    setDropdownWidth(Math.max(opsW));
  }, [parametersOptions]);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const parametersRes = await fetchSettings("inspection_parameters");
        setParametersOptions(parametersRes.items || []);
      } catch (err) {
        setParametersOptions([]);
      }
    }
    fetchDropdowns();
  }, []);

  const handleCloseDropdown = () => {
    if (!addMode) {
      onClose();
    } else if (isEditMode) {
      setIsEditMode(false);
    } else {
      onClose();
    }
  };

  const handlePrintClick = () => {
    if (!rights.can_print) {
      displayToast(`You don't have permission to print ${category}`, "error");
      return;
    }
    const fileName = "invoice";

    navigate(`/${tab}/${category}/transactions/${fileName}`, {
      state: {
        formData: {
          headDetails: headData,
          itemDetails: rows,
          rowData: rows || [],
          transactionCode: transactionCode,
          selectedReportType: selectedFieldName,
        },
      },
    });
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        parameter: "",
        parameter_value: null,
        tolerence: null,
        value_1: null,
        value_2: null,
        value_3: null,
        value_4: null,
        value_5: null,
        status: "",
      },
    ]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleRemoveRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const values = Object.values(row);
      const hasEmpty = values.some(
        (value) =>
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "")
      );

      if (hasEmpty) {
        showLocalToast(
          `All fields are required in every row. Please fill missing fields.`,
          "error"
        );
        return;
      }
    }

    if (!headData.product_id) {
      showLocalToast(`Product is required.`, "error");
      return;
    }

    setIsSaving(true);
    try {
      const head_data = {
        ...headData,
        quantity: headData.quantity ? parseInt(headData.quantity, 10) : null,
        product_id: headData.product_id
          ? parseInt(headData.product_id, 10)
          : null,
        transaction: selectedFieldName,
      };

      const item_data = rows.map((row, idx) => ({
        serial_no: idx + 1,
        ...row,
      }));
      const submissionData = { head_data, item_data };
      let response;
      if (jobCardCreated && isEditMode && headData?.id) {
        response = await updateInspectionReport(submissionData, headData.id);
      } else {
        response = await createInspectionReport(submissionData);
      }
      if (response && !response.description && !response.message) {
        displayToast(
          jobCardCreated && isEditMode
            ? "Inspection report updated successfully"
            : "Inspection report created successfully",
          "success"
        );

        setInspectionPopupState((prev) => ({
          ...prev,
          [selectedFieldName]: true,
        }));

        if (onClose) onClose();
      } else {
        displayToast(
          response?.description ||
            response.message ||
            "Failed to save inspection report",
          "error"
        );
      }
    } catch (err) {
      displayToast("Failed to save inspection report", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const popupRef = useOutsideClick(() => {
    if (onClose) onClose();
  });

  const getDropdownFixedStyle = (triggerEl) => {
    if (!triggerEl) return { left: 0, top: 0, width: 200 };
    const rect = triggerEl.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
    };
  };

  const isJobCardFinished = formData?.primaryDetails?.status === "finished";

  return (
    <div className="inspection-report-popup-overlay">
      <div className="inspection-report-popup-container" ref={popupRef}>
        {localToast.message && (
          <div className={`process-card-toast ${localToast.type}`}>
            {localToast.message}
          </div>
        )}
        <div className="inspection-report-popup-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList
              size={24}
              color="#ffffff"
              style={{ marginRight: 4 }}
            />
            {capitalizeFirstLetterOfEachWord(selectedFieldName)}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {jobCardCreated && !isEditMode && !isJobCardFinished && (
              <Edit2
                style={{ cursor: "pointer" }}
                size={22}
                onClick={() => setIsEditMode(true)}
                title="Edit Inspection Report"
              />
            )}
            {jobCardCreated && !isEditMode && headData.id && (
              <Printer
                style={{ cursor: "pointer" }}
                size={22}
                title="Edit Inspection Report"
                onClick={handlePrintClick}
              />
            )}
            {onClose && (
              <button
                className="inspection-report-popup-close"
                onClick={handleCloseDropdown}
              >
                &times;
              </button>
            )}
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="inspection-report-popup-head-section ">
              <div className="inspection-report-popup-head-field">
                <label>Job Card No.</label>
                <div className="inspection-report-popup-head-value min-line">
                  {jobCardNo || "-"}
                </div>
              </div>
              <div className="inspection-report-popup-head-field">
                <label>Product Name</label>
                <div className="inspection-report-popup-head-value min-line">
                  {inspectionHeadData?.product_name || "-"}
                </div>
              </div>
              <div className="inspection-report-popup-head-field">
                <label>Product Description</label>
                <div className="inspection-report-popup-head-value min-line">
                  {inspectionHeadData?.product_desc || "-"}
                </div>
              </div>
              <div className="inspection-report-popup-head-field">
                <label>Total Quantity</label>
                <div className="inspection-report-popup-head-value min-line">
                  {inspectionHeadData?.total_quantity || "-"}
                </div>
              </div>
            </div>
            <div className="transaction-card">
              <div
                className="transaction-card-content"
                style={{ position: "relative" }}
              >
                <div className="table-container">
                  <table className="transaction-table">
                    <thead>
                      <tr>
                        <th>Sl.No</th>
                        <th>Parameters</th>
                        <th>Parameter Value(mm)</th>
                        <th>Tolerance</th>
                        {Array.from({ length: 5 }, (_, i) => (
                          <th key={`value_${i + 1}`}>{`Value ${i + 1}`}</th>
                        ))}
                        <th>Status </th>
                        <th>Action </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className={`modal-dropdown-container`}
                              style={{
                                position: "relative",
                                width: dropdownWidth,
                              }}
                            >
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current[`parameter-${idx}`] =
                                    el)
                                }
                                className={`customer-data-modal-input master-data-modal-select-wrapper${
                                  openDropdown.row === idx &&
                                  openDropdown.field === "parameter"
                                    ? " dropdown-open-highlight"
                                    : ""
                                }`}
                                style={{
                                  width: dropdownWidth,
                                  pointerEvents:
                                    !jobCardCreated || isEditMode
                                      ? "auto"
                                      : "none",
                                  background:
                                    jobCardCreated && !isEditMode
                                      ? "#f9fafb"
                                      : undefined,
                                }}
                                onClick={() => {
                                  if (!jobCardCreated || isEditMode)
                                    setOpenDropdown(
                                      openDropdown.row === idx &&
                                        openDropdown.field === "parameter"
                                        ? { row: null, field: null }
                                        : { row: idx, field: "parameter" }
                                    );
                                }}
                                tabIndex={0}
                              >
                                <span className="modal-dropdown-text">
                                  {row.parameter || "Select Parameter"}
                                </span>
                                {(!jobCardCreated || isEditMode) && (
                                  <span className="dropdown-icon">▼</span>
                                )}
                              </div>
                              {openDropdown.row === idx &&
                                openDropdown.field === "parameter" &&
                                dropdownRefs.current[`parameter-${idx}`] && (
                                  <div
                                    ref={dropdownMenuRef}
                                    className="primary-dropdown-options inspection-report-popup-dropdown-fixed"
                                    style={getDropdownFixedStyle(
                                      dropdownRefs.current[`parameter-${idx}`]
                                    )}
                                  >
                                    {parametersOptions.map((opt) => (
                                      <div
                                        key={opt.id}
                                        className={`primary-dropdown-option${
                                          row.operations === opt.name
                                            ? " selected-option"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          handleInputChange(
                                            idx,
                                            "parameter",
                                            opt.name
                                          );
                                          setOpenDropdown({
                                            row: null,
                                            field: null,
                                          });
                                        }}
                                      >
                                        {opt.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              step={0.01}
                              value={row.parameter_value}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "parameter_value",
                                  e.target.value === ""
                                    ? null
                                    : parseFloat(e.target.value)
                                )
                              }
                              className="custom-input"
                              placeholder="Parameter Value"
                              disabled={jobCardCreated && !isEditMode}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step={1}
                              value={row.tolerence}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "tolerence",
                                  e.target.value === ""
                                    ? null
                                    : parseFloat(e.target.value)
                                )
                              }
                              className="custom-input"
                              placeholder="Tolerence"
                              disabled={jobCardCreated && !isEditMode}
                            />
                          </td>
                          {[
                            "value_1",
                            "value_2",
                            "value_3",
                            "value_4",
                            "value_5",
                          ].map((key) => (
                            <td key={key}>
                              <input
                                type="number"
                                step="any"
                                value={row[key] ?? ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    idx,
                                    key,
                                    e.target.value === ""
                                      ? null
                                      : parseFloat(e.target.value)
                                  )
                                }
                                className="custom-input"
                                placeholder={key
                                  .replace("_", " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                                disabled={jobCardCreated && !isEditMode}
                              />
                            </td>
                          ))}
                          <td>
                            <div
                              className={`modal-dropdown-container`}
                              style={{
                                position: "relative",
                                width: dropdownWidth,
                              }}
                            >
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current[`status-${idx}`] = el)
                                }
                                className={`customer-data-modal-input master-data-modal-select-wrapper${
                                  openDropdown.row === idx &&
                                  openDropdown.field === "status"
                                    ? " dropdown-open-highlight"
                                    : ""
                                }`}
                                style={{
                                  width: dropdownWidth,
                                  pointerEvents:
                                    !jobCardCreated || isEditMode
                                      ? "auto"
                                      : "none",
                                  background:
                                    jobCardCreated && !isEditMode
                                      ? "#f9fafb"
                                      : undefined,
                                }}
                                onClick={() => {
                                  if (!jobCardCreated || isEditMode)
                                    setOpenDropdown(
                                      openDropdown.row === idx &&
                                        openDropdown.field === "status"
                                        ? { row: null, field: null }
                                        : { row: idx, field: "status" }
                                    );
                                }}
                                tabIndex={0}
                              >
                                <span className="modal-dropdown-text">
                                  {row.status || "Select Status"}
                                </span>
                                {(!jobCardCreated || isEditMode) && (
                                  <span className="dropdown-icon">▼</span>
                                )}
                              </div>
                              {openDropdown.row === idx &&
                                openDropdown.field === "status" &&
                                dropdownRefs.current[`parameter-${idx}`] && (
                                  <div
                                    ref={dropdownMenuRef}
                                    className="primary-dropdown-options inspection-report-popup-dropdown-fixed"
                                    style={getDropdownFixedStyle(
                                      dropdownRefs.current[`status-${idx}`]
                                    )}
                                  >
                                    {[
                                      { label: "Accept", value: "accepted" },
                                      { label: "Reject", value: "rejected" },
                                    ].map((opt, index) => (
                                      <div
                                        key={index}
                                        className={`primary-dropdown-option${
                                          row.status === opt.value
                                            ? " selected-option"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          handleInputChange(
                                            idx,
                                            "status",
                                            opt.value
                                          );
                                          setOpenDropdown({
                                            row: null,
                                            field: null,
                                          });
                                        }}
                                      >
                                        {opt.label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          </td>
                          <td>
                            <button
                              className="remove-row-btn"
                              onClick={() => handleRemoveRow(idx)}
                              disabled={rows.length === 1 || !isEditMode}
                              title="Remove Row"
                            >
                              &times;
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="add-item-container-process-card">
                  <button
                    className="add-item-btn-process-card"
                    onClick={handleAddRow}
                    disabled={!isEditMode}
                  >
                    Add Item
                    <svg
                      className="plus-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
                <div className="table-save-button-container-process-card">
                  <button
                    className="next-btn-process-card"
                    onClick={handleSave}
                    disabled={isSaving || (jobCardCreated && !isEditMode)}
                  >
                    {isSaving ? "Saving..." : "SAVE"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InspectionCardPopup;
